import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProgressBar from '../../components/base/ProgressBar';
import { ScoreBadge } from '../../components/base/Badge';
import { useProgress } from '../../context/ProgressContext';
import { useAuth } from '../../context/useAuth';
import { fetchAllCourses, getMyEnrollmentsApi } from '../../api/apiServices';

export default function DashboardPage() {
  const { user, isAuthenticated } = useAuth();
  const { 
    enrollments, 
    isEnrolled, 
    completeLesson, 
    isLessonCompleted, 
    getQuizResult, 
    getLessonProgress 
  } = useProgress();

  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);           // All published courses
  const [myEnrollmentsData, setMyEnrollmentsData] = useState([]); // Enriched enrollments
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load both courses and user's enrollments
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!isAuthenticated || !user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all published courses
        const coursesRes = await fetchAllCourses();
        let allCourses = [];
        if (coursesRes.success && Array.isArray(coursesRes.courses)) {
          allCourses = coursesRes.courses.filter(c => c.isPublished === true);
          setCourses(allCourses);
        }

        // Fetch user's enrollments (real data from backend)
        const enrollRes = await getMyEnrollmentsApi();
        let userEnrollments = [];

        if (enrollRes.success && Array.isArray(enrollRes.enrollments)) {
          userEnrollments = enrollRes.enrollments;
        }

        // Enrich enrollments with course details
        const enriched = userEnrollments.map(enrollment => {
          const course = allCourses.find(c => 
            String(c._id) === String(enrollment.courseId?._id || enrollment.courseId)
          );
          return {
            enrollment,
            course: course || null
          };
        }).filter(item => item.course); // Remove enrollments without matching course

        setMyEnrollmentsData(enriched);

      } catch (err) {
        console.error("Dashboard data load error:", err);
        setError("Failed to load your dashboard. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-5 py-2 bg-teal-600 text-white rounded-xl"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const completedCount = myEnrollmentsData.filter(item => item.enrollment.courseCompleted).length;
  const inProgressCount = myEnrollmentsData.length - completedCount;

  // Calculate average score safely
  let totalScore = 0;
  let scoreCount = 0;

  myEnrollmentsData.forEach(({ enrollment }) => {
    if (enrollment.quizResults && enrollment.quizResults.length > 0) {
      enrollment.quizResults.forEach(r => {
        if (r.score) {
          totalScore += r.score;
          scoreCount++;
        }
      });
    }
  });

  const avgScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-16">
        {/* Welcome Header */}
        <div className="bg-white border-b border-slate-100 py-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-5">
              <img 
                src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.userName || 'User'}`}
                alt={user?.userName || 'Student'} 
                className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow" 
              />
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Welcome back, {user?.userName || user?.email?.split('@')[0] || 'Student'}!
                </h1>
                <p className="text-slate-500 mt-1">Continue your learning journey</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { 
                label: 'Enrolled Courses', 
                value: myEnrollmentsData.length, 
                icon: 'ri-book-open-line', 
                color: 'bg-teal-50 text-teal-600' 
              },
              { 
                label: 'In Progress', 
                value: inProgressCount, 
                icon: 'ri-loader-4-line', 
                color: 'bg-amber-50 text-amber-600' 
              },
              { 
                label: 'Completed', 
                value: completedCount, 
                icon: 'ri-check-double-line', 
                color: 'bg-emerald-50 text-emerald-600' 
              },
              { 
                label: 'Quizzes Taken', 
                value: myEnrollmentsData.reduce((sum, item) => sum + (item.enrollment.quizResults?.length || 0), 0), 
                icon: 'ri-quiz-line', 
                color: 'bg-purple-50 text-purple-600' 
              },
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className={`w-11 h-11 flex items-center justify-center rounded-2xl ${stat.color} mb-4`}>
                  <i className={`${stat.icon} text-2xl`} />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Main Content */}
          {myEnrollmentsData.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-100 py-20 text-center">
              <div className="w-20 h-20 mx-auto bg-teal-50 rounded-full flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-5xl text-teal-500" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-800 mb-2">No courses yet</h3>
              <p className="text-slate-500 mb-8 max-w-md mx-auto">
                Start your learning journey by enrolling in a course from our collection.
              </p>
              <Link 
                to="/courses" 
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-2xl transition"
              >
                Browse Available Courses
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* My Courses - Left Side */}
              <div className="lg:col-span-8">
                <h2 className="text-xl font-bold text-slate-900 mb-6">My Courses</h2>
                
                <div className="space-y-6">
                  {myEnrollmentsData.map(({ enrollment, course }) => {
                    const progress = getLessonProgress(course._id || course.id, course.sections ? 
                      course.sections.reduce((acc, s) => acc + (s.lectures?.length || 0), 0) : 0);

                    const lastLessonId = enrollment.lastLessonId;
                    let lastLesson = null;

                    if (lastLessonId && course.sections) {
                      for (let section of course.sections) {
                        const found = section.lectures?.find(l => String(l._id) === String(lastLessonId));
                        if (found) {
                          lastLesson = found;
                          break;
                        }
                      }
                    }

                    return (
                      <div key={course._id} className="bg-white rounded-2xl border border-slate-100 p-6 flex gap-6 hover:shadow-md transition">
                        <img 
                          src={course.thumbnail} 
                          alt={course.title} 
                          className="w-32 h-24 rounded-xl object-cover flex-shrink-0" 
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <h3 className="font-semibold text-lg text-slate-900 leading-tight">{course.title}</h3>
                            {enrollment.courseCompleted && (
                              <span className="px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Completed</span>
                            )}
                          </div>

                          <p className="text-sm text-slate-500 mt-1">{course.instructor}</p>

                          <div className="mt-4 mb-3">
                            <ProgressBar value={progress} showLabel size="sm" />
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">
                              {enrollment.completedLessons?.length || 0} lessons completed
                            </span>

                            {enrollment.courseCompleted ? (
                              <Link 
                                to={`/certificate/${course._id}`} 
                                className="text-amber-600 font-medium flex items-center gap-1 hover:text-amber-700"
                              >
                                <i className="ri-award-line" /> View Certificate
                              </Link>
                            ) : (
                              <Link 
                                to={`/courses/${course._id}/lesson/${lastLesson?._id || ''}`}
                                className="text-teal-600 font-medium flex items-center gap-1 hover:text-teal-700"
                              >
                                Continue Learning →
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-4 space-y-8">
                {/* Average Score Card */}
                {avgScore > 0 && (
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-3xl p-8 text-center">
                    <p className="text-teal-100 text-sm font-medium">YOUR AVERAGE SCORE</p>
                    <p className="text-6xl font-bold mt-2 mb-1">{avgScore}</p>
                    <p className="text-teal-100">across all quizzes</p>
                    <ScoreBadge score={avgScore} className="mt-4 mx-auto" />
                  </div>
                )}

                {/* Recent Quiz Activity */}
                <div className="bg-white rounded-3xl border border-slate-100 p-6">
                  <h3 className="font-semibold text-slate-900 mb-5">Recent Quiz Activity</h3>
                  <div className="space-y-4">
                    {myEnrollmentsData.flatMap(({ course, enrollment }) =>
                      (enrollment.quizResults || []).slice(-3).map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {r.isFinalQuiz ? "Final Quiz" : "Lesson Quiz"}
                            </p>
                            <p className="text-xs text-slate-500">{course.title}</p>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-slate-900">{r.score}%</span>
                            <ScoreBadge score={r.score} size="sm" />
                          </div>
                        </div>
                      ))
                    )}
                    {myEnrollmentsData.every(item => !item.enrollment.quizResults?.length) && (
                      <p className="text-slate-400 text-center py-6 text-sm">No quiz activity yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}